import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useHapticFeedback } from '../hooks/useTelegram';
import { useUser } from '../contexts/UserContext';
import './SBPLinkingPage.css';

interface NSPKBank {
  bankName: string;
  logoURL: string;
  memberId: string;
}

type LinkingStep = 'form' | 'submitting' | 'code' | 'verifying' | 'success' | 'error';

type SbpLinkingLocationState = {
  pendingNftId?: string | null;
  returnPathAfterSbp?: string | null;
};

function isSafeNftDetailReturnPath(path: string): boolean {
  return /^\/nft\/\d+$/.test(path);
}

// Format up to 10 digits as `(XXX) XXX-XX-XX` (without country code).
function formatPhoneBody(digits: string): string {
  const d = digits.slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length <= 8) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 8)}-${d.slice(8, 10)}`;
}

export const SBPLinkingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { notification } = useHapticFeedback();
  const { pendingActor } = useUser();
  const state = location.state as SbpLinkingLocationState | null;
  const pendingNftId = state?.pendingNftId || null;
  const returnPathAfterSbp =
    state?.returnPathAfterSbp && isSafeNftDetailReturnPath(state.returnPathAfterSbp)
      ? state.returnPathAfterSbp
      : null;

  const [phone, setPhone] = useState('+7 ');
  const [error, setError] = useState('');
  const [step, setStep] = useState<LinkingStep>('form');
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  // True when phone was prefilled from a previously SMS-verified source
  // (Max -> Telegram link flow). Locks the input so users don't accidentally
  // change a confirmed number; can be released via the "use different number"
  // link below the field.
  const [phoneIsLockedFromAuth, setPhoneIsLockedFromAuth] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement | null>(null);

  // Bank list from NSPK
  const [banks, setBanks] = useState<NSPKBank[]>([]);
  const [banksLoading, setBanksLoading] = useState(true);
  const [bankSearch, _setBankSearch] = useState('');
  const [selectedBank, setSelectedBank] = useState<NSPKBank | null>(null);
  const [showAllBanks, setShowAllBanks] = useState(false);
  const INITIAL_BANKS_COUNT = 15;

  // SMS code verification state
  const [smsCode, setSmsCode] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [lockCountdown, setLockCountdown] = useState(0);
  const [resendCountdown, setResendCountdown] = useState(180); // 3 minutes
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lockTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([null, null, null, null]);
  const proxyInputRef = useRef<HTMLInputElement | null>(null);

  // Load bank list from bundled c2bmembers.json on mount
  useEffect(() => {
    const loadBanks = async () => {
      try {
        const bankList = await apiClient.fetchNSPKBanks();
        setBanks(bankList);
      } catch (err) {
        console.error('Failed to load NSPK bank list:', err);
        setError('Не удалось загрузить список банков');
      } finally {
        setBanksLoading(false);
      }
    };
    loadBanks();
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
      if (lockTimerRef.current) clearInterval(lockTimerRef.current);
    };
  }, []);

  // Prefill phone with the SMS-verified value carried on the Max pending actor
  // (set after Max -> Telegram link completes with status `not_found`). The
  // backend stores it as 10 digits; reformat into the masked `+7 (...)` shape.
  useEffect(() => {
    if (pendingActor?.platform !== 'max') return;
    const verified = pendingActor.phone;
    if (!verified || !/^\d{10}$/.test(verified)) return;
    setPhone(`+7 ${formatPhoneBody(verified)}`);
    setPhoneIsLockedFromAuth(true);
  }, [pendingActor?.platform, pendingActor?.phone]);

  // Resend countdown timer
  useEffect(() => {
    if (step === 'code' && resendCountdown > 0) {
      resendTimerRef.current = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            if (resendTimerRef.current) clearInterval(resendTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => {
        if (resendTimerRef.current) clearInterval(resendTimerRef.current);
      };
    }
  }, [step, resendCountdown > 0 ? 'active' : 'inactive']);

  // Lock countdown timer
  useEffect(() => {
    if (lockedUntil) {
      const updateLockCountdown = () => {
        const remaining = Math.max(0, Math.ceil((lockedUntil * 1000 - Date.now()) / 1000));
        setLockCountdown(remaining);
        if (remaining <= 0) {
          setLockedUntil(null);
          setAttemptsLeft(3);
          if (lockTimerRef.current) clearInterval(lockTimerRef.current);
        }
      };
      updateLockCountdown();
      lockTimerRef.current = setInterval(updateLockCountdown, 1000);
      return () => {
        if (lockTimerRef.current) clearInterval(lockTimerRef.current);
      };
    }
  }, [lockedUntil]);

  // Transfer focus from proxy input to first code digit when entering code step
  useEffect(() => {
    if (step === 'code') {
      setTimeout(() => {
        codeInputRefs.current[0]?.focus();
      }, 50);
    }
  }, [step]);

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const allDigits = raw.replace(/\D/g, '');
    let body: string;
    if (allDigits.startsWith('7') || allDigits.startsWith('8')) {
      body = allDigits.slice(1);
    } else {
      body = allDigits;
    }
    const formatted = formatPhoneBody(body);
    setPhone(formatted ? `+7 ${formatted}` : '+7 ');
    setError('');
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    if (e.key === 'Backspace' && (input.selectionStart ?? 0) <= 3) {
      e.preventDefault();
    }
  };

  const handlePhoneFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.target;
    setTimeout(() => {
      input.selectionStart = input.selectionEnd = input.value.length;
    }, 0);
  };

  const getPhoneDigits = (): string => {
    const allDigits = phone.replace(/\D/g, '');
    return allDigits.startsWith('7') ? allDigits.slice(1) : allDigits;
  };

  const validateForm = (): boolean => {
    if (getPhoneDigits().length < 10) {
      setError('Введите полный номер телефона');
      return false;
    }
    if (!selectedBank) {
      setError('Выберите банк');
      return false;
    }
    return true;
  };

  const filteredBanks = banks.filter((bank) =>
    bank.bankName.toLowerCase().includes(bankSearch.toLowerCase())
  );

  const visibleBanks = showAllBanks
    ? filteredBanks
    : filteredBanks.slice(0, INITIAL_BANKS_COUNT);
  const hasMoreBanks = !showAllBanks && filteredBanks.length > INITIAL_BANKS_COUNT;

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatLockTime = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h} ч ${m} мин`;
    }
    return `${m} мин ${s} сек`;
  };

  // Submit: initiate linking via Kazna /pay API
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    // Focus proxy input within the user gesture so mobile keyboard opens
    proxyInputRef.current?.focus();

    setStep('submitting');
    setError('');

    try {
      const digits = getPhoneDigits();
      await apiClient.initiateSBPLinking(digits, selectedBank!.memberId);

      // Link received — go to code verification step
      setStep('code');
      setSmsCode('');
      setAttemptsLeft(3);
      setResendCountdown(180);
    } catch (err: any) {
      console.error('Failed to initiate SBP linking:', err);
      const responseData = err?.response?.data;

      // Check if user is locked out
      if (err?.response?.status === 429 && responseData?.locked_until) {
        setLockedUntil(responseData.locked_until);
        setStep('code');
        setAttemptsLeft(0);
        return;
      }

      const message = responseData?.message || 'Ошибка при привязке СБП. Попробуйте позже.';
      setError(message);
      setStep('error');
      notification('error');
    }
  }, [phone, selectedBank, notification]);

  // Verify the SMS code
  const handleVerifyCode = useCallback(async () => {
    if (smsCode.length !== 4) {
      setError('Введите 4-значный код');
      return;
    }

    setStep('verifying');
    setError('');

    try {
      const digits = getPhoneDigits();
      const result = await apiClient.verifySBPCode(
        smsCode,
        digits,
        selectedBank!.memberId,
        selectedBank!.bankName
      );

      if (result.success) {
        setStep('success');
        notification('success');

        // Return to NFT detail when user opened linking from purchase/upgrade on that card
        if (returnPathAfterSbp) {
          setTimeout(() => navigate(returnPathAfterSbp), 2000);
          return;
        }

        // Handle pending White List reward claim (ClaimRewardPage — no returnPathAfterSbp)
        if (pendingNftId) {
          try {
            const claimResult = await apiClient.claimWhiteListReward(pendingNftId);
            if (claimResult.success) {
              setTimeout(() => navigate('/white-list', { state: { fromSbpLinking: true } }), 2000);
              return;
            }
          } catch (claimErr) {
            console.error('Failed to claim NFT after SBP linking:', claimErr);
          }
        }

        // Default redirect
        setTimeout(async () => {
          try {
            const stats = await apiClient.getWhiteListStats();
            const whiteListStartDate = stats.white_list_start_date ? new Date(stats.white_list_start_date) : null;
            const now = new Date();
            if (whiteListStartDate && now < whiteListStartDate) {
              navigate('/white-list', { state: { fromSbpLinking: true } });
            } else {
              navigate('/');
            }
          } catch {
            navigate('/');
          }
        }, 2000);
      }
    } catch (err: any) {
      console.error('Failed to verify SBP code:', err);
      const responseData = err?.response?.data;

      // Handle lockout
      if (err?.response?.status === 429 && responseData?.locked_until) {
        setLockedUntil(responseData.locked_until);
        setAttemptsLeft(0);
        setError(responseData.message || 'Слишком много попыток');
        setStep('code');
        notification('error');
        return;
      }

      // Handle wrong code with attempts tracking
      if (responseData?.attempts_left !== undefined) {
        setAttemptsLeft(responseData.attempts_left);
      }

      setError(responseData?.message || 'Ошибка проверки кода');
      setStep('code');
      setSmsCode('');
      notification('error');
    }
  }, [smsCode, selectedBank, navigate, notification, pendingNftId, returnPathAfterSbp]);

  // Resend code
  const handleResendCode = useCallback(async () => {
    if (resendCountdown > 0) return;

    try {
      const digits = getPhoneDigits();
      const result = await apiClient.resendSBPCode(digits);
      if (result.success) {
        setResendCountdown(result.resend_cooldown_seconds || 180);
        setAttemptsLeft(3);
        setError('');
        setSmsCode('');
        notification('success');
      }
    } catch (err: any) {
      const responseData = err?.response?.data;
      if (responseData?.remaining_seconds) {
        setResendCountdown(responseData.remaining_seconds);
      }
      console.error('Failed to resend code:', err);
    }
  }, [resendCountdown, notification, phone]);

  const handleDigitChange = (index: number, value: string) => {
    const digits = value.replace(/\D/g, '');

    // iOS autofill sends the full code into one input
    if (digits.length > 1) {
      const code = digits.slice(0, 4);
      setSmsCode(code);
      setError('');
      const focusIndex = Math.min(code.length, 3);
      codeInputRefs.current[focusIndex]?.focus();
      return;
    }

    const digit = digits.slice(-1);
    const newCode = smsCode.split('');
    newCode[index] = digit;
    const joined = newCode.join('').slice(0, 4);
    setSmsCode(joined);
    setError('');
    if (digit && index < 3) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !smsCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter' && smsCode.length === 4) {
      handleVerifyCode();
    }
  };

  const handleDigitPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted) {
      setSmsCode(pasted);
      setError('');
      const focusIndex = Math.min(pasted.length, 3);
      codeInputRefs.current[focusIndex]?.focus();
    }
  };

  const handleBack = () => {
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    if (lockTimerRef.current) clearInterval(lockTimerRef.current);
    navigate(-1);
  };

  const handleRetry = () => {
    setStep('form');
    setError('');
    setSmsCode('');
    setAttemptsLeft(3);
    setLockedUntil(null);
    setSelectedBank(null);
  };

  const handleBackToForm = () => {
    setStep('form');
    setError('');
    setSmsCode('');
  };

  return (
    <div className="sbp-linking-page">
      {/* Hidden input to keep mobile keyboard open during async step transitions */}
      <input
        ref={proxyInputRef}
        inputMode="numeric"
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: 'fixed', top: '-9999px', opacity: 0, pointerEvents: 'none' }}
        readOnly
      />
      <div className="sbp-linking-header">
        <h1 className="sbp-linking-title">Привязка СБП</h1>
      </div>

      <div className="sbp-linking-content">
        {/* Step: Form -- phone + bank selection */}
        {step === 'form' && (
          <>
            <p className="sbp-linking-description">
              Введите номер телефона и выберите банк для привязки СБП
            </p>

            <div className="sbp-input-group">
              <input
                ref={phoneInputRef}
                type="tel"
                className={`sbp-phone-input ${error && !selectedBank ? 'sbp-phone-input-error' : ''} ${phoneIsLockedFromAuth ? 'sbp-phone-input-locked' : ''}`}
                placeholder="+7 (___) ___-__-__"
                value={phone}
                onChange={handlePhoneChange}
                onKeyDown={handlePhoneKeyDown}
                onFocus={(e) => { handlePhoneFocus(e); setIsPhoneFocused(true); }}
                onBlur={() => setIsPhoneFocused(false)}
                readOnly={phoneIsLockedFromAuth}
                aria-readonly={phoneIsLockedFromAuth}
              />
              {phoneIsLockedFromAuth && (
                <div className="sbp-phone-locked-hint">
                  <span className="sbp-phone-locked-text">Номер подтверждён при авторизации</span>
                  <button
                    type="button"
                    className="sbp-phone-locked-change"
                    onClick={() => {
                      setPhoneIsLockedFromAuth(false);
                      setPhone('+7 ');
                      setError('');
                      setTimeout(() => phoneInputRef.current?.focus(), 0);
                    }}
                  >
                    Использовать другой номер
                  </button>
                </div>
              )}
            </div>

            {/* Bank selector */}
            <div className="sbp-bank-section">
              <label className="sbp-input-label">Выберите банк</label>

              {banksLoading ? (
                <div className="sbp-bank-loading">
                  <div className="sbp-progress-spinner" />
                  <span>Загрузка списка банков...</span>
                </div>
              ) : (
                <div className="sbp-bank-list">
                  {visibleBanks.map((bank) => (
                    <label
                      key={bank.memberId}
                      className={`sbp-bank-item ${selectedBank?.memberId === bank.memberId ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedBank(bank);
                        setError('');
                      }}
                    >
                      <div className="sbp-bank-info">
                        <img
                          src={bank.logoURL}
                          alt={bank.bankName}
                          className="sbp-bank-logo"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <span className="sbp-bank-name">{bank.bankName}</span>
                      </div>
                      <input
                        type="radio"
                        name="sbp-bank"
                        value={bank.memberId}
                        checked={selectedBank?.memberId === bank.memberId}
                        onChange={() => setSelectedBank(bank)}
                        className="sbp-bank-radio"
                      />
                      <span className="sbp-bank-radio-custom">
                        {selectedBank?.memberId === bank.memberId && (
                          <span className="sbp-bank-radio-dot" />
                        )}
                      </span>
                    </label>
                  ))}
                  {hasMoreBanks && (
                    <button
                      type="button"
                      className="sbp-bank-show-more"
                      onClick={() => setShowAllBanks(true)}
                    >
                      Показать ещё ({filteredBanks.length - INITIAL_BANKS_COUNT})
                    </button>
                  )}
                  {filteredBanks.length === 0 && (
                    <div className="sbp-bank-empty">Банк не найден</div>
                  )}
                </div>
              )}
            </div>

            {error && <span className="sbp-input-error">{error}</span>}

            <div className={`sbp-form-actions ${isPhoneFocused ? 'sbp-form-actions-keyboard' : ''}`}>
              <button
                className="sbp-submit-button button--accent"
                onClick={handleSubmit}
                disabled={getPhoneDigits().length < 10 || !selectedBank}
              >
                Привязать СБП
              </button>

              <button
                className="sbp-back-button-secondary"
                onClick={handleBack}
              >
                Назад
              </button>
            </div>
          </>
        )}

        {/* Step: Submitting */}
        {step === 'submitting' && (
          <div className="sbp-linking-status-section">
            <div className="sbp-progress-spinner" />
            <p className="sbp-progress-text">Отправка запроса...</p>
          </div>
        )}

        {/* Step: SMS Code verification */}
        {step === 'code' && (
          <div className="sbp-code-section">
            <h2 className="sbp-code-title">Введите код из SMS</h2>
            <p className="sbp-code-description">
              Мы отправили код подтверждения на номер
            </p>
            <p className="sbp-code-phone">{phone}</p>

            {/* Lock message */}
            {lockedUntil && lockCountdown > 0 ? (
              <div className="sbp-code-locked">
                <div className="sbp-code-locked-icon">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="24" fill="#EF4444" opacity="0.15"/>
                    <path d="M18 22V18C18 14.69 20.69 12 24 12C27.31 12 30 14.69 30 18V22" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                    <rect x="16" y="22" width="16" height="14" rx="2" fill="#EF4444" opacity="0.3" stroke="#EF4444" strokeWidth="2"/>
                    <circle cx="24" cy="29" r="2" fill="#EF4444"/>
                  </svg>
                </div>
                <p className="sbp-code-locked-text">
                  Превышено количество попыток
                </p>
                <p className="sbp-code-locked-timer">
                  Повторите через {formatLockTime(lockCountdown)}
                </p>
              </div>
            ) : (
              <>
                {/* Code input */}
                <div className="sbp-code-input-group">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className={`sbp-code-digit-wrapper ${error ? 'sbp-code-digit-error' : ''}`}>
                      <input
                        ref={(el) => { codeInputRefs.current[i] = el; }}
                        type="text"
                        className="sbp-code-digit"
                        value={smsCode[i] || ''}
                        onChange={(e) => handleDigitChange(i, e.target.value)}
                        onKeyDown={(e) => handleDigitKeyDown(i, e)}
                        onPaste={handleDigitPaste}
                        maxLength={i === 0 ? undefined : 1}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoComplete={i === 0 ? 'one-time-code' : 'off'}
                        autoFocus={i === 0}
                      />
                    </div>
                  ))}
                </div>

                {error && attemptsLeft > 0 && (
                  <span className="sbp-code-error">
                    Неверный код: осталось {attemptsLeft} {attemptsLeft === 1 ? 'попытка' : 'попытки'}
                  </span>
                )}
                {error && attemptsLeft === 0 && (
                  <span className="sbp-code-error">{error}</span>
                )}

                {/* Resend link with countdown */}
                <div className="sbp-code-resend">
                  {resendCountdown > 0 ? (
                    <span className="sbp-code-resend-timer">
                      Отправить новый код через {formatTime(resendCountdown)}
                    </span>
                  ) : (
                    <button
                      className="sbp-code-resend-button"
                      onClick={handleResendCode}
                    >
                      Отправить новый код
                    </button>
                  )}
                </div>

                <button
                  className="sbp-submit-button button--accent"
                  onClick={handleVerifyCode}
                  disabled={smsCode.length !== 4}
                >
                  Подтвердить
                </button>
              </>
            )}

            {!(lockedUntil && lockCountdown > 0) && (
              <button
                className="sbp-back-button-secondary"
                onClick={handleBackToForm}
              >
                Изменить номер
              </button>
            )}
          </div>
        )}

        {/* Step: Verifying code */}
        {step === 'verifying' && (
          <div className="sbp-linking-status-section">
            <div className="sbp-progress-spinner" />
            <p className="sbp-progress-text">Проверяем код...</p>
          </div>
        )}

        {/* Step: Success */}
        {step === 'success' && (
          <div className="sbp-linking-status-section">
            <div className="sbp-success-icon">
              <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="30" cy="30" r="30" fill="#DCFCE7"/>
                <circle cx="30" cy="30" r="12.5" stroke="#22C55E" strokeWidth="1.5" fill="none"/>
                <path d="M24 30L28.5 34.5L36 25.5" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="sbp-success-text">СБП успешно привязан!</p>
            <p className="sbp-success-description">Перенаправляем обратно на страницу...</p>
          </div>
        )}

        {/* Step: Error */}
        {step === 'error' && (
          <>
            <div className="sbp-linking-status-section">
              <div className="sbp-error-icon">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="30" cy="30" r="30" fill="#FEE2E2"/>
                  <circle cx="30" cy="30" r="12.5" stroke="#EF4444" strokeWidth="1.5" fill="none"/>
                  <path d="M30 24V32" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
                  <circle cx="30" cy="36" r="1.2" fill="#EF4444"/>
                </svg>
              </div>
              <p className="sbp-error-text">СБП не привязан</p>
              <p className="sbp-error-description">Произошла ошибка, попробуйте ещё раз</p>
            </div>

            <div className="sbp-form-actions">
              <button
                className="sbp-submit-button button--accent"
                onClick={handleRetry}
              >
                Попробовать снова
              </button>

              <button
                className="sbp-back-button-secondary"
                onClick={handleBack}
              >
                Назад
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
